import React, { useEffect, useState } from 'react';
import { getInstallationStats } from '../../lib/analytics';
import { BarChart3, Activity, Globe } from 'lucide-react';

interface Stats {
  total: number;
  active: number;
  installations: Array<{
    domain: string;
    installedAt: Date;
    lastPing: Date;
    active: boolean;
  }>;
}

const InstallationStats: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await getInstallationStats();
        setStats(data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center text-gray-600">
        Failed to load installation statistics
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <Globe className="w-5 h-5 text-gray-700" />
            <h3 className="text-lg font-medium text-gray-900">Total Installations</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <Activity className="w-5 h-5 text-gray-700" />
            <h3 className="text-lg font-medium text-gray-900">Active Sites</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.active}</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <BarChart3 className="w-5 h-5 text-gray-700" />
            <h3 className="text-lg font-medium text-gray-900">Activity Rate</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {((stats.active / stats.total) * 100).toFixed(1)}%
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-100">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Installations</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Domain</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Installed</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Last Active</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {stats.installations.map((install) => (
                  <tr key={install.domain} className="border-b border-gray-100">
                    <td className="py-3 px-4 text-sm text-gray-900">{install.domain}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {new Date(install.installedAt).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {new Date(install.lastPing).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          install.active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {install.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallationStats;